/**
 * Release Regressions List
 *
 * Table of regression test results for a Release QA run
 */

import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { DataTable, LoadingOrbit, Text, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
import { CheckCircle } from '@phosphor-icons/react';
import { useReleaseRunRegressions } from '../useReleases';
import type { ReleaseRegressionItem, ReleaseRegressionStatus } from '../releaseTypes';
import { getRegressionStatusMeta } from '../releaseTypes';
import { useEligibilityModel } from '../../../hooks/useEligibilityModel';

const formatDuration = (durationMs: number | null): string => {
  if (!durationMs) return '-';

  if (durationMs < 1000) return `${durationMs}ms`;
  if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
  return `${(durationMs / 60000).toFixed(1)}m`;
};

const RegressionStatusBadge: React.FC<{ status: ReleaseRegressionStatus }> = ({ status }) => {
  const meta = getRegressionStatusMeta(status);
  const variantMap: Record<string, 'success' | 'destructive' | 'warning' | 'secondary'> = {
    green: 'success',
    red: 'destructive',
    yellow: 'warning',
    gray: 'secondary',
  };
  return (
    <TagBadge
      name={meta.label}
      color={badgeVariantToTagColor(variantMap[meta.color])}
      hideDot
      size="sm"
    />
  );
};

type ReleaseRegressionsListProps = {
  releaseRunId: string;
};

export const ReleaseRegressionsList: React.FC<ReleaseRegressionsListProps> = ({ releaseRunId }) => {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const runnableWord = useEligibilityModel() ? 'eligible' : 'promoted';

  const regressionsQuery = useReleaseRunRegressions(releaseRunId, { page, pageSize });

  const columns: ColumnDef<StockFeatures, ReleaseRegressionItem>[] = React.useMemo(
    () => [
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <RegressionStatusBadge status={row.original.status} />,
        meta: { flex: 0.8 },
      },
      {
        accessorKey: 'workflowName',
        header: 'Workflow',
        cell: ({ row }) => (
          <Text size="sm" className="font-medium truncate">
            {row.original.workflowName}
          </Text>
        ),
        meta: { flex: 2 },
      },
      {
        id: 'duration',
        header: 'Duration',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {formatDuration(row.original.durationMs)}
          </Text>
        ),
        meta: { flex: 0.8 },
      },
      {
        accessorKey: 'errorMessage',
        header: 'Error',
        cell: ({ row }) => (
          <Text
            size="sm"
            className={
              row.original.errorMessage ? 'text-destructive truncate' : 'text-muted-foreground'
            }
          >
            {row.original.errorMessage || '-'}
          </Text>
        ),
        meta: { flex: 2 },
      },
      {
        accessorKey: 'createdAt',
        header: 'Tested At',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {new Date(row.original.createdAt).toLocaleString()}
          </Text>
        ),
        meta: { flex: 1.5 },
      },
    ],
    []
  );

  if (regressionsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingOrbit size="lg" />
      </div>
    );
  }

  if (!regressionsQuery.data?.regressions.length) {
    return (
      <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
        <CheckCircle size={48} className="mx-auto text-muted-foreground mb-4" />
        <Text className="text-muted-foreground">No regression tests</Text>
        <Text size="sm" className="text-muted-foreground mt-1">
          Regression tests run {runnableWord} workflows to verify existing functionality
        </Text>
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={regressionsQuery.data.regressions}
      aria-label="Release regressions table"
      manualPagination
      pageCount={regressionsQuery.data.pagination.totalPages}
      pageIndex={page - 1}
      pageSize={pageSize}
      onPaginationChange={(newPageIndex, newPageSize) => {
        setPage(newPageIndex + 1);
        setPageSize(newPageSize);
      }}
      expandableRows={{
        render: (row) => {
          const regression = row.original;

          if (regression.status === 'pending') {
            return (
              <div className="py-4 text-center">
                <Text size="sm" className="text-muted-foreground">
                  Queued — result pending
                </Text>
              </div>
            );
          }

          if (!regression.errorMessage) {
            return (
              <div className="py-4 text-center">
                <Text size="sm" className="text-success">
                  Workflow completed successfully
                </Text>
              </div>
            );
          }

          return (
            <div className="space-y-2">
              <Text size="xs" className="text-muted-foreground font-medium">
                Error Details
              </Text>
              <div className="p-3 bg-destructive/10 rounded-md border border-destructive/20">
                <Text size="sm" className="text-destructive whitespace-pre-wrap">
                  {regression.errorMessage}
                </Text>
              </div>
            </div>
          );
        },
        single: true,
      }}
    />
  );
};
